import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { alSalirMal, type ApiError } from './api';
import { Icon } from './components/ui';
import { texto } from './i18n';

/**
 * Los mensajes del panel, en un solo lugar y siempre a la vista.
 *
 * ── Qué estaba pasando ────────────────────────────────────────────────────────
 * Cada pantalla mostraba sus errores donde le quedaba cómodo: un cartel arriba del formulario,
 * un párrafo rojo al lado de un campo, un `window.alert` del navegador. Y el formulario de
 * «nueva influencer» es largo: el botón está abajo, el cartel salía arriba, y al pulsar **no
 * pasaba nada visible**. El mensaje estaba ahí, a dos pantallas de scroll.
 *
 * Un mensaje que no se ve no es un mensaje. Es peor que no tenerlo, porque el sistema cree que
 * avisó.
 *
 * ── Por qué flotante y no «subir el scroll» ───────────────────────────────────
 * Mover el scroll de alguien es quitarle el control de dónde está mirando, y encima no siempre
 * alcanza: si el error tarda en llegar, para entonces ya volvió a bajar. Un aviso fijo se ve
 * desde cualquier posición y no obliga a nada.
 *
 * Abajo a la derecha y no arriba: arriba está la barra de navegación, y un aviso que la tapa
 * esconde justamente el menú al que uno va después de leerlo.
 *
 * ── Qué se va solo y qué no ───────────────────────────────────────────────────
 * Lo que salió bien se va a los cinco segundos: ya se ve en la pantalla que funcionó, el aviso
 * sólo lo confirma. Lo que salió mal **se queda hasta que lo cierres**. Un error suele traer
 * una instrucción —«andá a Credenciales y cargá…»— y hacerla desaparecer sola convierte el
 * aviso en un parpadeo que hay que provocar de nuevo para volver a leer.
 */

export type TipoAviso = 'error' | 'ok' | 'info';

type Aviso = {
  id: number;
  tipo: TipoAviso;
  mensaje: string;
  /** El código del error, cuando viene de la API. Sirve para reportar, no para entender. */
  codigo?: string;
  /** Las violaciones de política, que explican *qué* falló cuando el mensaje es genérico. */
  detalles?: string[];
};

type Emisor = {
  error: (mensaje: string, extra?: { codigo?: string; detalles?: string[] }) => void;
  ok: (mensaje: string) => void;
  info: (mensaje: string) => void;
};

const AvisosContext = createContext<Emisor | null>(null);

/**
 * Emite un aviso desde cualquier pantalla.
 *
 * Fuera del proveedor no rompe: descarta. Es una decisión deliberada — un aviso es
 * información, no una operación, y ninguna pantalla debería fallar por no poder mostrar uno.
 */
export function useAviso(): Emisor {
  const emisor = useContext(AvisosContext);
  return (
    emisor ?? {
      error: () => undefined,
      ok: () => undefined,
      info: () => undefined,
    }
  );
}

const SEGUNDOS_VISIBLE = 5000;

export function ProveedorDeAvisos({ children }: { children: React.ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const quitar = useCallback((id: number) => {
    setAvisos((actuales) => actuales.filter((aviso) => aviso.id !== id));
  }, []);

  const agregar = useCallback(
    (aviso: Omit<Aviso, 'id'>) => {
      /**
       * El identificador sale de un contador, no de la hora.
       *
       * Dos avisos en el mismo milisegundo —lo normal cuando una acción dispara dos peticiones—
       * compartirían clave, y React reutilizaría el nodo del primero para el segundo: el
       * segundo mensaje no se vería nunca.
       */
      const id = siguienteId++;
      setAvisos((actuales) => [...actuales.slice(-3), { ...aviso, id }]);

      if (aviso.tipo !== 'error') {
        setTimeout(() => quitar(id), SEGUNDOS_VISIBLE);
      }
    },
    [quitar],
  );

  const emisor = useMemo<Emisor>(
    () => ({
      error: (mensaje, extra) =>
        agregar({
          tipo: 'error',
          mensaje,
          ...(extra?.codigo ? { codigo: extra.codigo } : {}),
          ...(extra?.detalles?.length ? { detalles: extra.detalles } : {}),
        }),
      ok: (mensaje) => agregar({ tipo: 'ok', mensaje }),
      info: (mensaje) => agregar({ tipo: 'info', mensaje }),
    }),
    [agregar],
  );

  /**
   * Todo error de la API aparece, sin que la pantalla tenga que acordarse.
   *
   * Es el mismo razonamiento que el del aislamiento en el servidor: un control que depende de
   * que cada llamada se acuerde de aplicarlo se rompe con la próxima que alguien escriba, y no
   * hay forma de notarlo. Acá el punto único es el cliente de la API — por donde pasan todas
   * las peticiones, incluidas las que todavía no existen.
   *
   * Sólo las que modifican algo. Un GET que falla ya tiene su lugar: la pantalla que no pudo
   * cargar lo muestra en su sitio, con su botón de reintentar, y duplicarlo en un aviso sería
   * decir dos veces lo mismo.
   */
  useEffect(
    () =>
      alSalirMal((error: ApiError) => {
        const violaciones = Array.isArray(error.details?.violations)
          ? (error.details.violations as { message: string }[]).map((v) => v.message)
          : [];
        emisor.error(error.message, { codigo: error.code, detalles: violaciones });
      }),
    [emisor],
  );

  return (
    <AvisosContext.Provider value={emisor}>
      {children}
      <div
        // `aria-live` para que un lector de pantalla lo anuncie: el aviso aparece lejos del
        // foco, así que sin esto alguien que navega por teclado no se entera de nada.
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
      >
        {avisos.map((aviso) => (
          <TarjetaDeAviso key={aviso.id} aviso={aviso} onClose={() => quitar(aviso.id)} />
        ))}
      </div>
    </AvisosContext.Provider>
  );
}

let siguienteId = 1;

const ESTILO: Record<
  TipoAviso,
  { borde: string; fondo: string; texto: string; icono: 'alert' | 'check' | 'info' }
> = {
  error: {
    borde: 'border-[var(--color-danger)]/40',
    fondo: 'bg-[var(--color-danger)]/10',
    texto: 'text-[var(--color-danger)]',
    icono: 'alert',
  },
  ok: {
    borde: 'border-[var(--color-ok)]/40',
    fondo: 'bg-[var(--color-ok)]/10',
    texto: 'text-[var(--color-ok)]',
    icono: 'check',
  },
  info: {
    borde: 'border-[var(--color-border-strong)]',
    fondo: 'bg-[var(--color-surface)]',
    texto: 'text-[var(--color-ink)]',
    icono: 'info',
  },
};

function TarjetaDeAviso({ aviso, onClose }: { aviso: Aviso; onClose: () => void }) {
  const estilo = ESTILO[aviso.tipo];

  return (
    <div
      role={aviso.tipo === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto w-full max-w-md rounded-2xl border ${estilo.borde} ${estilo.fondo} p-4 shadow-[var(--shadow-float)] backdrop-blur`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 shrink-0 ${estilo.texto}`}>
          <Icon name={estilo.icono} size={16} />
        </span>

        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${estilo.texto}`}>{aviso.mensaje}</p>

          {aviso.detalles && (
            <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-[var(--color-ink-muted)]">
              {aviso.detalles.map((detalle) => (
                <li key={detalle}>{detalle}</li>
              ))}
            </ul>
          )}

          {aviso.codigo && (
            <p className="mt-1 font-mono text-[0.65rem] text-[var(--color-ink-muted)]">
              {aviso.codigo}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={texto('comun.cerrarAviso')}
          className="shrink-0 rounded-lg p-1 text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-sunken)]"
        >
          <Icon name="close" size={14} />
        </button>
      </div>
    </div>
  );
}
